import { useCallback, useEffect, useRef, useState } from 'react'

// one document-level drag/drop router per page. attaches handlers to
// `document` via useEffect and removes them on unmount. the leaked-
// listener class that caused the post-6342a87 drag-drop regression
// (EvidenceSection's component-scoped drop zone competing with
// SystemDetailPage's page-root onDrop and both firing for the same
// .xlsx drop) is impossible by construction with this shape.
//
// `document`-level (vs window or React synthetic events on a wrapper
// div) is deliberate:
//   * catches drops on EVERY element on the page, including elements
//     that call stopPropagation downstream, child components can no
//     longer "win" a drop by accident.
//   * the browser's DEFAULT behaviour for an unhandled drop is to
//     navigate to the file. the router calls preventDefault on every
//     dragover so the drop never reaches the default handler, every
//     drop is either routed or explicitly rejected with a toast.
//
// callers pass a `classify(file)` that returns a DropDecision per
// file, plus per-action handlers. the hook returns the current
// overlay state for the page to render its full-screen affordance.

export type DropAction = 'parse' | 'evidence' | 'import' | 'reject'

export interface DropDecision {
  action: DropAction
  // message shown in the full-page overlay during dragover. pages
  // pre-compute the contextual bits (system name, page name) when
  // constructing the classifier.
  overlayMessage: string
  // for action='reject', the toast message the router emits on drop
  rejectReason?: string
}

export interface DropRouterOptions {
  // pure function: given a dragged file's metadata, classify it.
  // called per-file on drop. for the dragover overlay we only have
  // type/extension hints (DataTransfer.items don't expose filenames
  // during drag, security restriction), so the hook uses the first
  // item's `type` to compute a preview classification using a
  // synthetic File-like with empty name. pages whose overlay text
  // doesn't depend on filename can ignore that nuance.
  classify: (file: { name: string; type: string; size: number }) => DropDecision
  // one handler per action shape. hook routes drops through these.
  // if a page returns action='X' but doesn't supply handler X, the
  // router falls back to rejection with the overlayMessage as the
  // toast text, surfaces the wiring gap loudly rather than silently.
  onParse?:    (files: File[]) => void | Promise<void>
  onEvidence?: (files: File[]) => void | Promise<void>
  onImport?:   (file:  File)   => void | Promise<void>
  onReject?:   (message: string) => void
  // disable the router entirely (e.g. while a modal is open or
  // during an in-flight import). cleanup still runs.
  enabled?: boolean
}

export interface DropRouterState {
  dragging: boolean
  overlayMessage: string
}

export function useDocumentDropRouter(options: DropRouterOptions): DropRouterState {
  const { classify, onParse, onEvidence, onImport, onReject, enabled = true } = options
  const [dragging,       setDragging]       = useState(false)
  const [overlayMessage, setOverlayMessage] = useState('')
  // dragenter / dragleave fire on every child element traversal, a
  // counter tracks net depth so the overlay only clears when the
  // cursor truly leaves the document.
  const enterDepthRef = useRef(0)
  // stable refs so the effect doesn't re-bind on every render. the
  // identity of `classify` typically changes when the page state does
  // (e.g. activeTab) and re-binding the listeners on every keystroke
  // would mask real leak risks behind churn.
  const classifyRef = useRef(classify); classifyRef.current = classify
  const onParseRef    = useRef(onParse);    onParseRef.current    = onParse
  const onEvidenceRef = useRef(onEvidence); onEvidenceRef.current = onEvidence
  const onImportRef   = useRef(onImport);   onImportRef.current   = onImport
  const onRejectRef   = useRef(onReject);   onRejectRef.current   = onReject

  const handleReject = useCallback((message: string) => {
    if (onRejectRef.current) onRejectRef.current(message)
  }, [])

  useEffect(() => {
    if (!enabled) return

    const isFileDrag = (e: DragEvent): boolean => {
      const items = e.dataTransfer?.items
      if (!items) return false
      for (let i = 0; i < items.length; i++) {
        if (items[i].kind === 'file') return true
      }
      return false
    }

    const onDragEnter = (e: DragEvent) => {
      if (!isFileDrag(e)) return
      e.preventDefault()
      enterDepthRef.current++
      if (enterDepthRef.current === 1) {
        setDragging(true)
        // use the first item's MIME type as a hint for the overlay.
        // the classifier sees an empty filename, pages that care
        // about extension during dragover should rely on type.
        const first = e.dataTransfer?.items?.[0]
        const mime  = first?.type ?? ''
        const decision = classifyRef.current({ name: '', type: mime, size: 0 })
        setOverlayMessage(decision.overlayMessage)
      }
    }

    const onDragOver = (e: DragEvent) => {
      // the killer line: prevents the browser's default
      // "navigate-to-file" handling. every dragover must call this or
      // the drop event won't fire on us.
      if (!isFileDrag(e)) return
      e.preventDefault()
    }

    const onDragLeave = (e: DragEvent) => {
      if (!isFileDrag(e)) return
      enterDepthRef.current--
      if (enterDepthRef.current <= 0) {
        enterDepthRef.current = 0
        setDragging(false)
        setOverlayMessage('')
      }
    }

    const onDrop = (e: DragEvent) => {
      if (!isFileDrag(e)) return
      e.preventDefault()
      enterDepthRef.current = 0
      setDragging(false)
      setOverlayMessage('')
      const files = e.dataTransfer?.files ? Array.from(e.dataTransfer.files) : []
      if (files.length === 0) return

      // classify each file. bucket into per-action lists so callers
      // can batch their UX (e.g. "2 PDFs + 1 xlsx → 2 evidence uploads
      // + 1 parse").
      const parse:    File[] = []
      const evidence: File[] = []
      const imports:  File[] = []
      const rejects:  Array<{ file: File; reason: string }> = []
      for (const f of files) {
        const decision = classifyRef.current({ name: f.name, type: f.type, size: f.size })
        if (decision.action === 'parse')         parse.push(f)
        else if (decision.action === 'evidence') evidence.push(f)
        else if (decision.action === 'import')   imports.push(f)
        else rejects.push({ file: f, reason: decision.rejectReason ?? decision.overlayMessage })
      }

      // run rejections first so the user sees the explanation even
      // when other files in the same drop succeed.
      for (const r of rejects) handleReject(`${r.file.name}: ${r.reason}`)

      if (parse.length    > 0 && onParseRef.current)    void onParseRef.current(parse)
      if (evidence.length > 0 && onEvidenceRef.current) void onEvidenceRef.current(evidence)
      // import handler is single-file (the import-workbook flow is
      // one-at-a-time by design). multiple .xlsx in one drop → first
      // routed, others rejected.
      if (imports.length > 0 && onImportRef.current) {
        void onImportRef.current(imports[0])
        if (imports.length > 1) {
          for (let i = 1; i < imports.length; i++) {
            handleReject(`${imports[i].name}: only one workbook can be imported at a time`)
          }
        }
      }
    }

    document.addEventListener('dragenter', onDragEnter)
    document.addEventListener('dragover',  onDragOver)
    document.addEventListener('dragleave', onDragLeave)
    document.addEventListener('drop',      onDrop)
    return () => {
      document.removeEventListener('dragenter', onDragEnter)
      document.removeEventListener('dragover',  onDragOver)
      document.removeEventListener('dragleave', onDragLeave)
      document.removeEventListener('drop',      onDrop)
      enterDepthRef.current = 0
    }
  }, [enabled, handleReject])

  return { dragging, overlayMessage }
}
